import { useState } from 'react';

import SimpleModal from 'lib/modal/simple-modal';
import {
  SimpleModalContentStyled,
  SimpleModalVerticalActionsStyled,
} from 'lib/modal/simple-modal.styles';
import { NegativeButton } from 'approot/shared/styles/buttons';
import RadioInput from 'lib/form/radio-input/radio-input-new';
import { spacing16 } from 'approot/shared/styles/spacing.style';
import Text from 'approot/shared/styles/typography/text';
import { useLoggingMutation } from 'lib/apollo/apollo-hooks';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { DELETE_TEACHER_ANSWERS } from 'approot/activity-builder/present/activity-answers.graphql';
import type {
  DeleteTeacherAnswersMutation,
  DeleteTeacherAnswersMutationVariables,
} from '../../../present/activity-answers.graphql.generated';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import {
  deleteAnswersByComponentIdInCache,
  deleteAllAnswersInCache,
} from 'approot/activity-builder/present/activity-answer.apollo';

type Props = {
  isVisible: boolean;
  onClose: () => void;
  componentId: string;
  onCloseActivitySheet?: () => void;
};

const CLEAR_SINGLE = 'clear-single';
const CLEAR_ALL = 'clear-all';

const ClearModelledResponseModal = ({
  isVisible,
  onClose,
  componentId,
  onCloseActivitySheet,
}: Props) => {
  const [clearOption, setClearOption] = useState<string>(CLEAR_SINGLE);
  const { showSuccess, showError } = useSnackbar();

  const { contentId, contentType, version, userClassId } =
    useActivityBuilderPresentContext();

  const [deleteTeacherAnswers, { loading, client }] = useLoggingMutation<
    DeleteTeacherAnswersMutation,
    DeleteTeacherAnswersMutationVariables
  >(DELETE_TEACHER_ANSWERS, {
    onCompleted: () => {
      clearOption === CLEAR_SINGLE
        ? deleteAnswersByComponentIdInCache({
            client,
            componentId,
          })
        : deleteAllAnswersInCache({
            client,
          });

      const successMessage =
        clearOption === CLEAR_SINGLE
          ? 'Modelled response cleared.'
          : 'All modelled responses cleared for this lesson.';
      showSuccess(successMessage);
      onClose();
      onCloseActivitySheet?.();
    },
    onError: () => {
      showError('Something went wrong. Please try again.');
      onClose();
    },
  });

  const handleClear = () => {
    deleteTeacherAnswers({
      variables: {
        input: {
          contentId,
          classId: Number(userClassId),
          contentType,
          contentVersion: version || '',
          ...(clearOption === CLEAR_SINGLE ? { componentId } : {}),
        },
      },
    }).catch(() => {});
  };

  if (!version) {
    return null;
  }

  return (
    <SimpleModal
      isVisible={isVisible}
      title="Clear Modelled Responses?"
      headerTitle="Clear Modelled Responses?"
      size={456}
      onClose={onClose}
    >
      <SimpleModalContentStyled>
        <Text $marginBottom={spacing16}>
          Once cleared, your modelled responses are permanently deleted and
          cannot be restored. Student responses will not be affected.
        </Text>
        <RadioInput
          id={CLEAR_SINGLE}
          name="clear-option"
          label="Clear this modelled response only"
          checked={clearOption === CLEAR_SINGLE}
          onChange={() => setClearOption(CLEAR_SINGLE)}
          labelMarginBottom={spacing16}
        />
        <RadioInput
          id={CLEAR_ALL}
          name="clear-option"
          label="Clear all modelled responses for this lesson"
          checked={clearOption === CLEAR_ALL}
          onChange={() => setClearOption(CLEAR_ALL)}
        />
        <SimpleModalVerticalActionsStyled>
          <NegativeButton
            type="button"
            data-selector="clear-modelled-response"
            onClick={handleClear}
            disabled={loading}
            loading={loading}
          >
            Clear Modelled Response
          </NegativeButton>
        </SimpleModalVerticalActionsStyled>
      </SimpleModalContentStyled>
    </SimpleModal>
  );
};

export default ClearModelledResponseModal;
